import { GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { contactDisplay, leadDisplayName } from "@/lib/contact";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import LeadTagChips from "@/components/leadTags/LeadTagChips";
import QuienAtiendeBadge from "./QuienAtiendeBadge";
import {
  deriveBadge,
  leadNeedsReply,
  relativeFrom,
  sourceLabel,
  type TableroCardLead,
} from "./types";

type Props = {
  lead: TableroCardLead;
  selected?: boolean;
  draggable?: boolean;
  onClick: () => void;
  employeeNameById: Map<string, string>;
};

const ProspectoCard = ({ lead, selected, draggable, onClick, employeeNameById }: Props) => {
  // useSortable must be called unconditionally — when dragging is disabled we
  // just don't render the handle. Hook order stays stable.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lead.id,
    data: {
      pipelinePositionId: lead.pipelinePositionId,
      assignedAgentId: lead.assignedAgentId,
    },
    disabled: !draggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const requiereHumano = leadNeedsReply(lead);
  const badge = deriveBadge(lead);
  const asesor = lead.employeeId ? employeeNameById.get(lead.employeeId) : undefined;
  const displayName = leadDisplayName(lead);
  const contact = contactDisplay(lead);
  const fuente = sourceLabel(lead.source);
  const tooltipMsg =
    !lead.employeeId && requiereHumano
      ? "Sin asignar — requiere intervención humana"
      : "Esperando respuesta del asesor";

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={cn(
        "relative cursor-pointer rounded-md border bg-surface p-3 shadow-card transition-shadow hover:shadow-md",
        selected
          ? "border-2 border-palomma-primary bg-palomma-primary/5"
          : requiereHumano
            ? "border-danger ring-1 ring-danger/30"
            : "border-transparent"
      )}
    >
      {requiereHumano && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="absolute left-2 top-2 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-danger" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{tooltipMsg}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {draggable ? (
        <button
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="absolute right-2 top-2 cursor-grab text-neutral-300 hover:text-neutral-600 active:cursor-grabbing"
          aria-label="Mover"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      ) : null}
      <div
        className={cn(
          "truncate pr-6 text-sm font-semibold text-neutral-900",
          requiereHumano && "pl-4"
        )}
      >
        {displayName}
      </div>
      {contact.kind !== "none" && (
        <div className="mt-0.5 truncate text-xs text-neutral-600">{contact.value}</div>
      )}
      <div className="my-2 border-t border-neutral-100" />
      <div className="flex items-center gap-2 text-xs">
        {fuente ? (
          <span className="truncate rounded-pill bg-palomma-primary/10 px-2 py-0.5 text-[11px] font-medium text-palomma-primary">
            {fuente}
          </span>
        ) : null}
        <span className="shrink-0 text-neutral-600">
          {fuente ? "· " : ""}
          {relativeFrom(lead.lastLeadMessageAt)}
        </span>
      </div>
      <LeadTagChips tagIds={lead.tagIds} className="mt-2" />
      <div className="my-2 border-t border-neutral-100" />
      <div className="mb-2">
        <QuienAtiendeBadge badge={badge} asesor={asesor} />
      </div>
      {lead.lastMessagePreview ? (
        <div className="truncate text-xs italic text-neutral-600">{lead.lastMessagePreview}</div>
      ) : null}
    </div>
  );
};

export default ProspectoCard;
